import { Grid, Typography } from '@mui/material';
import { SubmitButton } from '@tassat/button';
import { CurrencyField } from '@tassat/formik-input';
import { stripPrefix } from '@tassat/utility';
import { Formik } from 'formik';
import React from 'react';
import ReactGA from 'react-ga4';

import useStyles from './styles';
import { validateRelease } from './submission';
import {
    IReleaseFormErrors,
    IReleaseFormProps,
    IReleaseFormValues,
    IReleaseFormikProps
} from './types';

const FormContents = (props: IReleaseFormProps) => {
    const classes = useStyles();
    const {
        formProps,
        errors,
        values,
        isSubmitting,
        isValid,
        setFieldValue,
        submitForm
    } = props;

    const {
        pledge,
        assetPair
    } = formProps;

    const securityObject = assetPair?.security;

    React.useEffect(() => {
        const realAssetAmount = stripPrefix(values.AssetAmount, `${securityObject?.Ticker} `);
        if (realAssetAmount === '.') {
            setFieldValue('AssetAmount', `${securityObject?.Ticker} 0.`);
        }
    }, [values.AssetAmount]);

    return (
        <form className={classes.flexColumn}>
            <Grid
                container
                direction='column'
                spacing={2}
            >
                <Grid
                    className={classes.fieldContainer}
                    item
                >
                    <Typography
                        className={classes.sectionTitle}
                        variant='h5'
                    >
                        <strong>Asset Amount</strong>
                    </Typography>
                    <CurrencyField
                        currencyDecimalPoint={6}
                        currencySymbol={securityObject?.Ticker ?? ccyObject?.Ticker}
                        errors={errors}
                        fieldSize='large'
                        label=''
                        name='AssetAmount'
                        placeholder={securityObject?.Ticker ?? ccyObject?.Ticker}
                    />
                    <Typography
                        className={classes.balanceText}
                        variant='h5'
                    >
                        {`Current Posted Amount: ${pledge.AssetAmount}`}
                    </Typography>
                </Grid>
            </Grid>
            <Grid
                className={classes.submitContainer}
                container
                direction='column'
                item
                justifyContent='center'
                spacing={0}
                xs={12}
            >
                <SubmitButton
                    disabled={isSubmitting || !isValid}
                    onClick={submitForm}
                    text='SUBMIT'
                    type='none'
                />
            </Grid>
            <Typography className={classes.legalText}>
                {'By pushing SUBMIT, I hereby certify that the information previously provided to Arca Institutional U.S. Treasury Fund in my Subscription Agreement (TFND), AML/KYC questionnaire and/or other documents provided to the TFND, as well as all account information, agreements and representations provided to tZERO Securities, LLC, remains accurate and complete in all respects as of the date hereof. Click here to access '}
                <a
                    href='https://storage.googleapis.com/lynq-docs/pdfs/arca_tfnd_ppm.pdf'
                    onClick={() => {
                        ReactGA.event('click', {
                            event_category: 'engagement',
                            event_label: 'tfnd_document_link'
                        });
                    }}
                    rel='noreferrer'
                    target='_blank'
                >
                    TFND&apos;s Private Placement Memorandum and Financial Statements.
                </a>
            </Typography>
        </form>
    );
};

export default function ReleaseForm(props: IReleaseFormikProps) {
    const { formProps } = props;
    const {
        handleRelease,
        assetPair,
        pledge
    } = formProps;

    const initialValues: IReleaseFormValues = { AssetAmount: '' };

    const validate = async (values: IReleaseFormValues) => {
        const errors: IReleaseFormErrors = {};

        await validateRelease(values, {
            errors,
            pledgeAvailableAssetAmount: pledge.AssetAmount,
            assetPair
        });

        return errors;
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values: IReleaseFormValues) => {
                if (handleRelease) {
                    await handleRelease(values);
                }
            }}
            validate={validate}
            validateOnChange
            validateOnMount
        >
            {(formContentsProps) => (
                <FormContents
                    {...formContentsProps}
                    formProps={formProps}
                />
            )}
        </Formik>
    );
}